/**
 * ============================================
 * USE ENVIRONMENT DATA HOOK
 * ============================================
 * 
 * Fetches ALL data for an environment from backend in one call.
 * Returns health, infrastructure, players, and remote config.
 * Clears data on connection errors.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { pollingConfig, type ServiceHealthStatus } from '@/config';
import { getEnvironmentAll, type EnvironmentAllData, type ServiceStatus } from '@/services/api';

export interface ServiceHealth {
  serviceId: string;
  status: ServiceHealthStatus;
  message: string;
  lastChecked: Date;
  details?: Record<string, unknown>;
}

export interface PlatformCombination {
  key: string;  // e.g., "0-5" for PC + PlayStation
  count: number;
}

interface UseEnvironmentDataResult {
  // Health
  servicesHealth: ServiceHealth[];
  overallStatus: ServiceHealthStatus;
  statusColor: string;
  healthyCount: number;
  totalCount: number;
  
  // Infrastructure
  infrastructure: {
    ec2Count: number;
    s3Objects: number;
    lambdaFunctions: number;
    asgInstances: number;
  };
  
  // Players
  totalPlayers: number;
  platformBreakdown: Record<string, number>;
  platformCombinations: PlatformCombination[];
  
  // Database
  clusterName?: string;

  // AWS
  awsOrgName?: string;
  
  // Remote Config
  remoteConfig: Array<{
    version: string;
    url: string;
    forceUpdate: boolean;
    environment: string;
  }>;
  
  // State
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function getStatusColor(status: ServiceHealthStatus): string {
  switch (status) {
    case 'healthy': return '#22c55e';
    case 'warning': return '#f59e0b';
    case 'critical': return '#ef4444';
    default: return '#6b7280';
  }
}

function convertServiceStatus(services: Record<string, ServiceStatus>): ServiceHealth[] {
  return Object.entries(services).map(([id, status]) => ({
    serviceId: id,
    status: status.status,
    message: status.message,
    lastChecked: new Date(status.checked_at || Date.now()),
    details: status.details,
  }));
}

export function useEnvironmentData(environmentId: string): UseEnvironmentDataResult {
  const [data, setData] = useState<EnvironmentAllData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const consecutiveFailures = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      console.log(`Fetching data for ${environmentId}...`);
      const result = await getEnvironmentAll(environmentId);
      setData(result);
      setError(null);
      consecutiveFailures.current = 0;
      console.log(`${environmentId} data fetched${result.loading ? ' (backend warming)' : ''}`);
      // Backend returns `loading: true` when its per-env cache hasn't been
      // populated yet — the response is a stub with empty services/players.
      // Keep isLoading=true in that case so the UI keeps its spinner rather
      // than rendering an empty "0 services" state; the polling effect will
      // switch to a fast retry until the stub clears.
      if (!result.loading) {
        setIsLoading(false);
      }
    } catch (err) {
      console.error(`Failed to fetch ${environmentId} data:`, err);
      consecutiveFailures.current += 1;
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);

      // After 2 consecutive failures, clear the stale data
      if (consecutiveFailures.current >= 2) {
        console.log(`Clearing stale data for ${environmentId} after ${consecutiveFailures.current} failures`);
        setData(null);
      }
      setIsLoading(false);
    }
  }, [environmentId]);

  // Initial fetch
  useEffect(() => {
    consecutiveFailures.current = 0;
    setIsLoading(true);
    setData(null); // Clear data when environment changes
    fetchData();
  }, [fetchData]);

  // Auto-refresh. While the backend reports it's still warming the cache
  // (data.loading === true) poll aggressively (2s) so the spinner clears
  // the instant real data is available. Otherwise fall back to the normal
  // 30s cadence to avoid hammering the API.
  const backendWarming = data?.loading === true;
  useEffect(() => {
    if (!pollingConfig.autoRefresh) return;

    const intervalMs = backendWarming ? 2000 : pollingConfig.dataRefresh;
    const interval = setInterval(fetchData, intervalMs);
    return () => clearInterval(interval);
  }, [fetchData, backendWarming]);

  // Parse data
  const servicesHealth = data?.health?.services 
    ? convertServiceStatus(data.health.services)
    : [];
  
  const healthyCount = servicesHealth.filter(s => s.status === 'healthy').length;
  const totalCount = servicesHealth.length;
  
  const overallStatus: ServiceHealthStatus = data?.health?.overall_status as ServiceHealthStatus || 'unknown';
  const statusColor = getStatusColor(overallStatus);

  // Parse platform combinations from backend
  const platformCombinations: PlatformCombination[] = [];
  const combos = data?.players?.platform_combinations;
  if (combos && typeof combos === 'object') {
    for (const [key, count] of Object.entries(combos)) {
      if (typeof count === 'number') {
        platformCombinations.push({ key, count });
      }
    }
    // Sort by count descending
    platformCombinations.sort((a, b) => b.count - a.count);
  }

  return {
    servicesHealth,
    overallStatus,
    statusColor,
    healthyCount,
    totalCount,
    
    infrastructure: {
      ec2Count: data?.infrastructure?.ec2_count || 0,
      s3Objects: data?.infrastructure?.s3_objects || 0,
      lambdaFunctions: data?.infrastructure?.lambda_functions || 0,
      asgInstances: data?.infrastructure?.asg_instances || 0,
    },
    
    totalPlayers: data?.players?.total_players || 0,
    platformBreakdown: data?.players?.platform_breakdown || {},
    platformCombinations,
    
    clusterName: data?.players?.cluster_name,
    awsOrgName: data?.aws_org_name,

    remoteConfig: data?.remote_config || [],
    
    isLoading,
    error,
    refetch: fetchData,
  };
}
